/* eslint-disable max-lines */
import { ActionButtonText } from '@strata/react-buttons';
import {
    MemberEscalationSubType,
    MemberInquiryEscalationOptionTree,
    RequestType,
} from '@strata/react-chat';
import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { DateInputFormik } from '@strata/react-date-time';
import { Hyperlink } from '@strata/react-hyperlink';
import { InlineMessage } from '@strata/react-inline-message';
import { TextInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';

import { getCheckRestrictedGroup, getTreatmentsForPatient } from 'api/apertureAPI';
import { PayerRequestWritePermissions } from 'services/constants';
import { handleError } from 'services/Error';
import { PBMOnlyOptions } from 'services/pbmOnlyOptions';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { useGetAllProductServicesQuery } from 'store/api/referenceApi';
import { type TreatmentDetails } from 'store/api/treatmentApi';

const validationSchemaShape = ({ restrictedAccess }: { restrictedAccess: boolean }) => ({
    requestType: Yup.string()
        .oneOf(Object.values(RequestType).filter((type) => type !== RequestType.ARCHIVE))
        .required(validationStrings.requiredField.errorMessage),
    requestSubType: Yup.string().when('requestType', {
        is: RequestType.ESCALATION,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    typeLevel2: Yup.string().when('requestType', {
        is: RequestType.ESCALATION,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    memberId: Yup.string().when('requestType', {
        is: RequestType.ESCALATION,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    firstName: Yup.string().when('requestType', {
        is: RequestType.ESCALATION,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    lastName: Yup.string().when('requestType', {
        is: RequestType.ESCALATION,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    dob: DateInputFormik.validator().when('requestType', {
        is: RequestType.ESCALATION,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    treatments: Yup.array(
        Yup.object({
            treatment_id: Yup.string().required(),
            product_service_oid: Yup.string().required(),
        }),
    ).when('requestType', {
        is: RequestType.ESCALATION,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    selectedTreatments: Yup.array(Yup.string().required()),
    ...(!restrictedAccess
        ? {
              groupId: Yup.string().when(['requestType', 'treatments'], {
                  is: (
                      requestType: (typeof RequestType)[keyof typeof RequestType],
                      treatments: { treatment_id: string; product_service_oid: string }[],
                  ) => requestType === RequestType.ESCALATION && treatments?.length === 0,
                  then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
              }),
              restrictedGroup: Yup.bool().when(['requestType', 'treatments'], {
                  is: (
                      requestType: (typeof RequestType)[keyof typeof RequestType],
                      treatments: { treatment_id: string; product_service_oid: string }[],
                  ) => requestType === RequestType.ESCALATION && treatments?.length === 0,
                  then: (schema) => schema.required().notOneOf([true]),
              }),
          }
        : undefined),
});

function requestSubTypeIncludes() {
    return Object.values(MemberEscalationSubType).filter(
        (val) =>
            !(
                [
                    // Compass only escalation sub types
                    MemberEscalationSubType.RESOLVE_AUTH_DATA_ISSUE,
                    MemberEscalationSubType.REQUEST_RATE_REVIEW,
                    MemberEscalationSubType.CONFIRM_MEMBER_COVERAGE,
                ] as (typeof MemberEscalationSubType)[keyof typeof MemberEscalationSubType][]
            ).includes(val),
    );
}

const allowedKeys = requestSubTypeIncludes();
const valuesOfRequestSubType = MemberInquiryEscalationOptionTree.filter((branch) =>
    allowedKeys.find((st) => st === branch.ticketType),
).map((item) => ({
    name: item.ticketType,
    secondaryData: item.typeDescription,
}));

const subTypeSelectValue = (name: string | undefined) =>
    // return a copy of the readonly data as a mutable to be
    // able to be used with select drop down.
    MemberInquiryEscalationOptionTree.find((item) => item.ticketType === name)?.typeLevel2?.map(
        (item) => ({ name: item.name, secondaryData: item.secondaryData }),
    );

interface MemberEscalationProps {
    openedFromTreatment: boolean;
}

// eslint-disable-next-line complexity
export const MemberEscalation = ({ openedFromTreatment }: MemberEscalationProps) => {
    const { restrictedAccess } = useAppSelector((state) => state.auth);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const validationSchema = Yup.object(validationSchemaShape({ restrictedAccess }));
    const formik = useFormikContext<Yup.InferType<typeof validationSchema>>();
    const { permissions } = useAppSelector((state) => state.auth);
    const showPBMOnlyRadioGroup = permissions.includes(
        PayerRequestWritePermissions.WRITE_PAYER_REQUEST_PBM_ONLY,
    );
    const { data: products } = useGetAllProductServicesQuery();

    return formik.values.requestType === RequestType.ESCALATION ? (
        <div className="margin__bottom--medium">
            <SuggestSingleSelectFormik
                id="requestSubType"
                label="Please Select One Of The Following"
                values={valuesOfRequestSubType}
                onChange={(newval) => {
                    void formik.setValues({
                        ...formik.values,
                        requestSubType: newval?.name,
                        typeLevel2:
                            newval?.name === formik.values.requestSubType
                                ? formik.values.typeLevel2
                                : undefined,
                    });
                }}
                required
                displaySecondaryDataBelowOption
            />
            {formik.values.requestSubType && (
                <SuggestSingleSelectFormik
                    id="typeLevel2"
                    label="Subtype"
                    values={subTypeSelectValue(formik.values.requestSubType)}
                    onChange={(selectedItem) => {
                        void formik.setFieldValue('typeLevel2', selectedItem?.name);
                    }}
                    required
                    displaySecondaryDataBelowOption
                />
            )}
            {showPBMOnlyRadioGroup && (
                <RadioButtonGroupFormik
                    name="pbmOnly"
                    label="Request Visibility"
                    options={PBMOnlyOptions}
                    required
                />
            )}
            <div className="sds_label font-semi-bold--uppercase margin__bottom--medium">
                Member Lookup
            </div>
            <TextInputFormik
                id="memberId"
                label="Member ID"
                required
                disabled={!!formik.values.treatments}
            />
            <div className="flex__gap--medium">
                <TextInputFormik
                    id="firstName"
                    label="Member First Name"
                    required
                    disabled={!!formik.values.treatments}
                />
                <TextInputFormik
                    id="lastName"
                    label="Member Last Name"
                    required
                    disabled={!!formik.values.treatments}
                />
                <DateInputFormik
                    id="dob"
                    label="Member DOB"
                    required
                    disabled={!!formik.values.treatments}
                />
            </div>
            {!restrictedAccess && formik.values.treatments?.length === 0 && (
                <>
                    <TextInputFormik
                        id="groupId"
                        label="Member Group ID"
                        required
                        disabled={formik.values.restrictedGroup != null}
                    />
                    {formik.values.restrictedGroup == null && (
                        <ActionButtonText
                            label="Submit Group ID"
                            id="submit-group-id-button"
                            className="margin__bottom--xx-small"
                            disabled={!!formik.errors.groupId}
                            onClick={() => {
                                void (async () => {
                                    try {
                                        const restrictedGroup = await getCheckRestrictedGroup({
                                            groupId: formik.values.groupId as string,
                                            memberId: formik.values.memberId,
                                            memberFirstName: formik.values.firstName,
                                            memberLastName: formik.values.lastName,
                                            memberDob: formik.values.dob!,
                                        });
                                        await formik.setFieldValue(
                                            'restrictedGroup',
                                            restrictedGroup,
                                        );
                                    } catch (err) {
                                        handleError(
                                            'An error occurred during member lookup.',
                                            err as Error,
                                        );
                                    }
                                })();
                            }}
                        />
                    )}
                    {formik.values.restrictedGroup === true && (
                        <div className="margin__bottom--medium">
                            <InlineMessage
                                wrapText
                                intent={InlineMessage.Intent.WARNING}
                                label="Restricted Access: You cannot create requests for restricted members."
                            />
                        </div>
                    )}
                </>
            )}
            {formik.values.treatments?.length === 0 &&
                (restrictedAccess || formik.values.restrictedGroup === false) && (
                    <div className="margin__bottom--medium">
                        <InlineMessage
                            wrapText
                            intent={InlineMessage.Intent.WARNING}
                            label="No treatments found for member."
                        />
                    </div>
                )}
            {!formik.values.treatments && (
                <ActionButtonText
                    label="Lookup Member"
                    id="lookup-member-button"
                    className="margin__bottom--medium"
                    disabled={
                        !!(
                            formik.errors.memberId ??
                            formik.errors.firstName ??
                            formik.errors.lastName ??
                            formik.errors.dob
                        )
                    }
                    onClick={() => {
                        void (async () => {
                            try {
                                const result = await getTreatmentsForPatient({
                                    memberId: formik.values.memberId,
                                    memberFirstName: formik.values.firstName,
                                    memberLastName: formik.values.lastName,
                                    memberDob: formik.values.dob!,
                                });
                                await formik.setFieldValue('treatments', result.treatments);
                            } catch (err) {
                                handleError(
                                    'An error occurred during member lookup.',
                                    err as Error,
                                );
                            }
                        })();
                    }}
                />
            )}
            {!!formik.values.treatments && !openedFromTreatment && (
                <ActionButtonText
                    label="Reset Member Lookup"
                    id="reset-member-lookup-button"
                    className="margin__bottom--medium"
                    onClick={() => {
                        void formik.setValues({
                            ...formik.values,
                            memberId: undefined,
                            firstName: undefined,
                            lastName: undefined,
                            dob: undefined,
                            treatments: undefined,
                            groupId: undefined,
                            restrictedGroup: undefined,
                        });
                    }}
                />
            )}
            {(formik.values.treatments ?? []).length > 0 && (
                <>
                    <div className="sds_label font-semi-bold--uppercase margin__bottom--small">
                        Select Treatments(s)
                    </div>
                    <CheckboxListFormik
                        name="selectedTreatments"
                        label=""
                        disabled={openedFromTreatment}
                        options={formik.values.treatments!.map((treatment) => ({
                            value: treatment.treatment_id,
                            component: (
                                <div
                                    className="flex__gap--small"
                                    style={{ display: 'inline-flex', alignItems: 'center' }}
                                >
                                    <span>
                                        {products?.productServiceMap[treatment.product_service_oid]}
                                    </span>
                                    <Hyperlink
                                        navUrl={AppRoutes.treatmentDetails(treatment.treatment_id)}
                                        openInNewTab
                                        linkText={<ActionButtonText label="View Treatment" />}
                                    />
                                </div>
                            ),
                        }))}
                    />
                </>
            )}
        </div>
    ) : null;
};

MemberEscalation.initialValues = (treatmentDetails: TreatmentDetails) => ({
    memberId: treatmentDetails.memberDetails.memberId,
    firstName: treatmentDetails.memberDetails.firstName,
    lastName: treatmentDetails.memberDetails.lastName,
    dob: treatmentDetails.memberDetails.dateOfBirth,
    treatments: [
        {
            treatment_id: treatmentDetails.treatmentId,
            product_service_oid: treatmentDetails.productServiceOid,
        },
    ],
    selectedTreatments: [treatmentDetails.treatmentId],
    groupId: treatmentDetails.memberDetails?.benefitGroup,
});

MemberEscalation.validationSchema = validationSchemaShape;
